import { initializePaddle, type Paddle } from "@paddle/paddle-js";
import { auth } from "../../Config/firebase-config";
import { LoginAPI, RefreshToken } from "../Auth/Auth";
import { GetSettings } from "../Settings/Setting";
import { GetAllTemplates } from "../Template/Template";
import { sendPostCallToSever } from "../Axios/Axios";
import type { AppDispatch } from "../../Redux/store";
import { invalidatePremiumStatusCache } from "../../hooks/premiumStatusCache";

export type SubscriptionPlan = "monthly" | "yearly";

type PaddleEnvironment = "sandbox" | "production";

type CheckoutEvent = {
  name?: string;
  data?: unknown;
  error?: unknown;
};

export type SubscriptionStatus = {
  isPremium: boolean;
  plan: SubscriptionPlan | null;
  subscriptionValue: unknown;
  source?: string;
  renewalDate?: string | null;
};

let paddlePromise: Promise<Paddle | undefined> | null = null;
const eventListeners = new Set<(event: CheckoutEvent) => void>();

const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const getPaddleEnvironment = (): PaddleEnvironment => {
  const raw =
    process.env.REACT_APP_PADDLE_ENVIRONMENT ??
    process.env.REACT_APP_ENVIRONMENT ??
    "sandbox";
  return String(raw).toLowerCase() === "production"
    ? "production"
    : "sandbox";
};

const getPaddleClientToken = () =>
  process.env.REACT_APP_PADDLE_CLIENT_TOKEN?.trim() ?? "";

const getSuccessUrl = () =>
  process.env.REACT_APP_PADDLE_SUCCESS_URL?.trim() || window.location.origin;

const getPriceIdForPlan = (plan: SubscriptionPlan) => {
  if (plan === "yearly") {
    return process.env.REACT_APP_PADDLE_YEARLY_PRICE_ID?.trim() ?? "";
  }

  return process.env.REACT_APP_PADDLE_MONTHLY_PRICE_ID?.trim() ?? "";
};

const inferPlan = (value: unknown): SubscriptionPlan | null => {
  const normalized = String(value ?? "").toLowerCase();

  if (
    normalized.includes("year") ||
    normalized.includes("annual") ||
    normalized.includes("annually")
  ) {
    return "yearly";
  }

  if (normalized.includes("month")) {
    return "monthly";
  }

  return null;
};

const hasActiveSubscription = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value > 0;
  }

  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  if (
    [
      "false",
      "0",
      "free",
      "none",
      "inactive",
      "cancelled",
      "canceled",
      "expired",
      "trial_expired",
    ].includes(normalized)
  ) {
    return false;
  }

  return true;
};

const readFallbackCustomAttributes = (): Record<string, unknown> => {
  const raw = (
    auth.currentUser as { reloadUserInfo?: { customAttributes?: string } } | null
  )?.reloadUserInfo?.customAttributes;

  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

const emitCheckoutEvent = (event: CheckoutEvent) => {
  eventListeners.forEach((listener) => {
    listener(event);
  });
};

const getReadablePaddleError = (value: unknown) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  const errorObj = value as Record<string, unknown>;
  return String(
    errorObj.detail ??
    errorObj.message ??
    errorObj.error ??
    errorObj.code ??
    ""
  ).trim();
};

const ensurePaddle = async (): Promise<Paddle> => {
  if (typeof window === "undefined") {
    throw new Error("Paddle checkout can only be opened in the browser.");
  }

  const clientToken = getPaddleClientToken();
  if (!clientToken) {
    throw new Error(
      "Missing REACT_APP_PADDLE_CLIENT_TOKEN. Add your Paddle client token to the environment."
    );
  }

  if (!paddlePromise) {
    paddlePromise = initializePaddle({
      environment: getPaddleEnvironment(),
      token: clientToken,
      eventCallback: (event) => {
        emitCheckoutEvent(event as CheckoutEvent);
      },
    });
  }

  const paddle = await paddlePromise;
  if (!paddle) {
    paddlePromise = null;
    throw new Error("Unable to initialize Paddle checkout.");
  }

  return paddle;
};

export const hasPaddleConfiguration = () =>
  Boolean(
    getPaddleClientToken() &&
    process.env.REACT_APP_PADDLE_MONTHLY_PRICE_ID &&
    process.env.REACT_APP_PADDLE_YEARLY_PRICE_ID
  );

export const getCurrentSubscriptionStatus = async (
  forceRefresh = false
): Promise<SubscriptionStatus> => {
  let tokenClaims: Record<string, unknown> = {};

  if (auth.currentUser) {
    try {
      const tokenResult = await auth.currentUser.getIdTokenResult(forceRefresh);
      tokenClaims = tokenResult.claims as Record<string, unknown>;
    } catch {
      tokenClaims = {};
    }
  }

  const mergedClaims = {
    ...readFallbackCustomAttributes(),
    ...tokenClaims,
  };

  const subscriptionValue =
    mergedClaims.subscription ??
    mergedClaims.subscriptionStatus ??
    mergedClaims.subscription_status ??
    mergedClaims.plan ??
    mergedClaims.subscriptionPlan ??
    mergedClaims.subscription_plan ??
    null;

  const plan =
    inferPlan(
      mergedClaims.plan ??
      mergedClaims.subscriptionPlan ??
      mergedClaims.subscription_plan ??
      subscriptionValue
    ) ?? null;

  const source = (
    mergedClaims.source ??
    mergedClaims.provider ??
    mergedClaims.subscriptionSource ??
    mergedClaims.subscription_source ??
    mergedClaims.subscriptionProvider ??
    mergedClaims.subscription_provider ??
    ""
  ).toString().toLowerCase();
  const renewalDate = (
    mergedClaims.subscriptionRenewalDate ??
    null
  ) as string | null;

  return {
    isPremium:
      hasActiveSubscription(subscriptionValue) ||
      String(mergedClaims.level ?? "").toLowerCase() === "pro",
    plan,
    subscriptionValue,
    source,
    renewalDate,
  };
};

export const startPaddleSubscriptionCheckout = async ({
  plan,
  email,
  userId,
}: {
  plan: SubscriptionPlan;
  email?: string | null;
  userId?: string | null;
}) => {
  const priceId = getPriceIdForPlan(plan);
  if (!priceId) {
    throw new Error(
      `Missing Paddle price ID for the ${plan} plan. Check your environment variables.`
    );
  }

  const paddle = await ensurePaddle();

  return new Promise<CheckoutEvent>((resolve, reject) => {
    let settled = false;

    const finish = (event: CheckoutEvent, shouldReject = false) => {
      if (settled) {
        return;
      }

      settled = true;
      eventListeners.delete(handleEvent);

      if (shouldReject) {
        reject(
          new Error(
            getReadablePaddleError(event.error ?? event.data) ||
            "Paddle checkout failed."
          )
        );
        return;
      }

      resolve(event);
    };

    const handleEvent = (event: CheckoutEvent) => {
      if (event.name === "checkout.completed") {
        finish(event);
      } else if (event.name === "checkout.error") {
        finish(event, true);
      } else if (event.name === "checkout.closed") {
        finish(event);
      }
    };

    eventListeners.add(handleEvent);

    try {
      paddle.Checkout.open({
        settings: {
          displayMode: "overlay",
          theme: "light",
          locale: "en",
          allowLogout: false,
          successUrl: getSuccessUrl(),
        },
        items: [
          {
            priceId,
            quantity: 1,
          },
        ],
        customer: email ? { email } : undefined,
        customData: {
          app: "ezy-watermark-web",
          billing_plan: plan,
          firebase_uid: userId ?? "",
        },
      });
    } catch (error) {
      eventListeners.delete(handleEvent);
      reject(
        error instanceof Error
          ? error
          : new Error("Unable to open Paddle checkout.")
      );
    }
  });
};

export const refreshAfterSuccessfulSubscription = async (
  dispatch: AppDispatch
) => {
  invalidatePremiumStatusCache();
  try {
    await dispatch(LoginAPI() as any);
  } catch {
    // Best-effort refresh. Claims are primarily updated by backend webhook handling.
  }

  let latestStatus = await getCurrentSubscriptionStatus(true);

  for (let attempt = 0; attempt < 8; attempt += 1) {
    // Force Firebase to re-fetch a token with the latest custom claims from the server.
    // The Axios interceptor will pick up the updated token automatically on the next request.
    await RefreshToken();

    latestStatus = await getCurrentSubscriptionStatus(true);
    if (latestStatus.isPremium) {
      await Promise.all([
        dispatch(GetAllTemplates() as any),
        dispatch(GetSettings() as any),
      ]);
      return latestStatus;
    }

    await delay(1500);
  }

  return latestStatus;
};

export const cancelPaddleSubscriptionAPI = () => {
  return sendPostCallToSever({}, "billing/cancel-subscription");
};
